// src/components/partner/AddTeamMemberModal.jsx

import {

  useEffect,

  useMemo,

  useState,

} from "react";

import {
  Mail,
  Send,
  UserPlus,
  UserRound,
  X,
} from "lucide-react";

import { sendPartnerMemberInvitation } from "../../lib/partner/partnerMemberInvitations";



const ROLE_LABELS = {

  owner: "Owner",

  admin: "Administrator",

  cohort_manager:

    "Cohort manager",

  facilitator: "Facilitator",

  viewer: "Viewer",

};



const ROLE_DESCRIPTIONS = {

  owner:

    "Full partner access, including managing other owners and administrators.",



  admin:

    "May manage the partner team, cohorts and learners.",



  cohort_manager:

    "May support and manage assigned cohorts and learners.",



  facilitator:

    "May view and support learners assigned to them within their assigned cohorts.",



  viewer:

    "Read-only access to permitted partner information.",

};



export default function AddTeamMemberModal({

  open,

  partnerId,

  partnerName,

  actorRole,

  allowOwner = false,

  defaultRole = "cohort_manager",

  onClose,

  onInvited,

}) {

  const [
  fullName,
  setFullName,
] = useState("");

  const [email, setEmail] =

    useState("");



  const [role, setRole] =

    useState(defaultRole);





  const [saving, setSaving] =

    useState(false);



  const [error, setError] =

    useState("");



  const availableRoles =

    useMemo(() => {

      const mayInviteOwner =

        allowOwner ||

        actorRole === "owner" ||

        actorRole ===

          "gwin_admin";



      return mayInviteOwner

      ? [

          "owner",

          "admin",

          "cohort_manager",

          "facilitator",

          "viewer",

        ]

      : [

          "admin",

          "cohort_manager",

          "facilitator",

          "viewer",

        ];

    }, [

      actorRole,

      allowOwner,

    ]);



  useEffect(() => {

    if (!open) return;



    const requestedDefault =

      availableRoles.includes(

        defaultRole

      )

        ? defaultRole

        : availableRoles[0];


    setFullName("");
    setEmail("");

    setRole(

      requestedDefault

    );

    setSaving(false);

    setError("");

  }, [

    open,

    defaultRole,

    availableRoles,

  ]);



  useEffect(() => {

    if (!open) {

      return undefined;

    }



    function handleEscape(event) {

      if (

        event.key === "Escape" &&

        !saving

      ) {

        onClose?.();

      }

    }



    window.addEventListener(

      "keydown",

      handleEscape

    );



    return () => {

      window.removeEventListener(

        "keydown",

        handleEscape

      );

    };

  }, [

    open,

    saving,

    onClose,

  ]);



  if (!open) return null;



  function handleBackdropClick(

    event

  ) {

    if (

      event.target ===

        event.currentTarget &&

      !saving

    ) {

      onClose?.();

    }

  }



  async function handleSubmit(

    event

  ) {

    event.preventDefault();



    try {

      setSaving(true);

      setError("");

      const cleanFullName =
  String(fullName || "")
    .trim()
    .replace(/\s+/g, " ");

const cleanEmail =
  String(email || "")
    .trim()
    .toLowerCase();

if (!cleanFullName) {
  throw new Error(
    "Please enter the team member's full name."
  );
}

if (
  cleanFullName.length > 100
) {
  throw new Error(
    "The team member's full name cannot exceed 100 characters."
  );
}

if (
  !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    cleanEmail
  )
) {
  throw new Error(
    "Please enter a valid team member email address."
  );
}



      const result =
  await sendPartnerMemberInvitation({
    partnerId,

    name:
      cleanFullName,

    email:
      cleanEmail,

    role,
  });



      await onInvited?.(
  result,
  {
    name:
      cleanFullName,

    email:
      cleanEmail,

    role,
  }
);



      onClose?.();

    } catch (submitError) {

      console.error(

        "Sending partner team invitation failed:",

        submitError

      );



      setError(

        submitError?.message ||

          "The invitation could not be sent."

      );

    } finally {

      setSaving(false);

    }

  }



  return (

    <div

      role="presentation"

      onMouseDown={

        handleBackdropClick

      }

      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-6"

    >

      <div

        role="dialog"

        aria-modal="true"

        aria-labelledby="invite-team-member-title"

        className="max-h-[95vh] w-full overflow-y-auto rounded-t-2xl border border-[#D7D7D7] bg-white shadow-2xl sm:max-w-lg sm:rounded-2xl"

      >

        <header className="flex items-start justify-between border-b border-[#D7D7D7] px-6 py-5">

          <div>

            <p className="text-xs font-black uppercase tracking-[0.14em] text-[#777777]">

              {partnerName ||

                "Partner organisation"}

            </p>



            <h2

              id="invite-team-member-title"

              className="mt-1 text-2xl font-black text-black"

            >

              Invite team member

            </h2>

          </div>



          <button

            type="button"

            onClick={onClose}

            disabled={saving}

            aria-label="Close invitation form"

            className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#D5D5D5] bg-white text-black transition hover:bg-[#F4F4F4] disabled:opacity-50"

          >

            <X className="h-5 w-5" />

          </button>

        </header>



        <form

          onSubmit={handleSubmit}

          className="space-y-5 p-6"

        >

          {error && (

            <div

              role="alert"

              className="rounded-lg border border-[#BDBDBD] bg-[#F4F4F4] px-4 py-3 text-sm font-semibold text-black"

            >

              {error}

            </div>

          )}



          <div className="rounded-lg border border-[#D7D7D7] bg-[#F4F4F4] p-4 text-sm leading-relaxed text-[#555555]">
  Add the team member's details
  below. They will receive their
  own secure invitation and
  create their own password
  before entering the Partner
  Portal. The invitation expires
  after seven days.
</div>
<div>
  <label
    htmlFor="team-member-name"
    className="block text-sm font-black text-black"
  >
    Full name
  </label>

  <div className="relative mt-2">
    <UserRound className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#777777]" />

    <input
      id="team-member-name"
      type="text"
      value={fullName}
      onChange={(event) => {
        setFullName(
          event.target.value
        );

        setError("");
      }}
      disabled={saving}
      required
      maxLength={100}
      autoComplete="name"
      placeholder="e.g. Sarah Jacobs"
      className="w-full rounded-lg border border-[#D5D5D5] bg-white py-3 pl-11 pr-4 text-sm text-black outline-none transition focus:border-black focus:ring-4 focus:ring-black/10 disabled:bg-[#F4F4F4]"
    />
  </div>
</div>


          <div>

            <label

              htmlFor="team-member-email"

              className="block text-sm font-black text-black"

            >

              Email address

            </label>



            <div className="relative mt-2">

              <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#777777]" />



              <input

                id="team-member-email"

                type="email"

                value={email}

                onChange={(event) =>

                  setEmail(

                    event.target.value

                  )

                }

                disabled={saving}

                required

                autoComplete="email"

                placeholder="team.member@example.com"

                className="w-full rounded-lg border border-[#D5D5D5] bg-white py-3 pl-11 pr-4 text-sm text-black outline-none transition focus:border-black focus:ring-4 focus:ring-black/10 disabled:bg-[#F4F4F4]"

              />

            </div>

          </div>



          <div>

            <label

              htmlFor="team-member-role"

              className="block text-sm font-black text-black"

            >

              Partner role

            </label>



            <select

              id="team-member-role"

              value={role}

              onChange={(event) =>

                setRole(

                  event.target.value

                )

              }

              disabled={saving}

              className="mt-2 w-full rounded-lg border border-[#D5D5D5] bg-white px-4 py-3 text-sm text-black outline-none transition focus:border-black focus:ring-4 focus:ring-black/10"

            >

              {availableRoles.map(

                (roleOption) => (

                  <option

                    key={roleOption}

                    value={roleOption}

                  >

                    {

                      ROLE_LABELS[

                        roleOption

                      ]

                    }

                  </option>

                )

              )}

            </select>



            <p className="mt-2 text-xs leading-relaxed text-[#777777]">

              {

                ROLE_DESCRIPTIONS[

                  role

                ]

              }

            </p>

          </div>



          <div className="flex flex-col-reverse gap-3 border-t border-[#D7D7D7] pt-5 sm:flex-row sm:justify-end">

            <button

              type="button"

              onClick={onClose}

              disabled={saving}

              className="rounded-lg border border-[#D5D5D5] bg-white px-5 py-3 text-sm font-black text-black transition hover:bg-[#F4F4F4] disabled:opacity-50"

            >

              Cancel

            </button>



            <button

              type="submit"

              disabled={saving}

              className="inline-flex items-center justify-center rounded-lg bg-black px-6 py-3 text-sm font-black text-white transition hover:bg-[#252525] disabled:cursor-not-allowed disabled:opacity-60"

            >

              {saving ? (

                <Send className="mr-2 h-4 w-4 animate-pulse" />

              ) : (

                <UserPlus className="mr-2 h-4 w-4" />

              )}



              {saving

                ? "Sending invitation…"

                : "Send invitation"}

            </button>

          </div>

        </form>

      </div>

    </div>

  );

}